import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useApiGet(endpoint) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}${endpoint}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [endpoint]);

  return { data, loading, error };
}

export function useApiPost(endpoint) {
  const [loading, setLoading] = useState(false);

  const post = useCallback(async (body) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}${endpoint}`, body);
      return res.data;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  return { post, loading };
}

export function useMultiGet(endpoints) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const stableKey = useMemo(() => JSON.stringify(endpoints), [endpoints]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    const parsed = JSON.parse(stableKey);
    const fetchAll = async () => {
      try {
        const results = await Promise.all(
          parsed.map((ep) => axios.get(`${API}${ep.path}`))
        );
        if (!cancelled) {
          const mapped = {};
          parsed.forEach((ep, i) => {
            mapped[ep.key] = results[i].data[ep.dataKey] || results[i].data;
          });
          setData(mapped);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [stableKey]);

  return { data, loading, error };
}

export { API };
